// Utility functions for security-related UI helpers

import { format, parseISO } from "date-fns";
import type { StatusTrendItem, SecurityIssueStats } from "@/services/securityIssuesService";

/**
 * Data structure for severity card display
 */
export interface IssueSeverityData {
  critical: number;
  high: number;
  medium: number;
  low: number;
  criticalTrend: number;
  highTrend: number;
  mediumTrend: number;
  lowTrend: number;
}

/**
 * Data structure for open issue age card display
 */
export interface OpenIssueAgeData {
  averageAge: number;
  ageDistribution: Array<{
    label: string;
    count: number;
    percentage: number;
    color: string;
  }>;
}

/**
 * Data structure for status trend chart display
 */
export interface StatusTrendData {
  day: number;
  date: string;
  opened: number;
  resolved: number;
  inReview: number;
}

/**
 * Transform API stats to severity card data format
 */
export function transformToSeverityData(stats: SecurityIssueStats | undefined): IssueSeverityData {
  if (!stats) {
    return {
      critical: 0,
      high: 0,
      medium: 0,
      low: 0,
      criticalTrend: 0,
      highTrend: 0,
      mediumTrend: 0,
      lowTrend: 0,
    };
  }
  return {
    critical: stats.severity_counts?.critical ?? 0,
    high: stats.severity_counts?.high ?? 0,
    medium: stats.severity_counts?.medium ?? 0,
    low: stats.severity_counts?.low ?? 0,
    criticalTrend: stats.severity_trends?.critical ?? 0,
    highTrend: stats.severity_trends?.high ?? 0,
    mediumTrend: stats.severity_trends?.medium ?? 0,
    lowTrend: stats.severity_trends?.low ?? 0,
  };
}

/**
 * Transform API stats to open issue age card data format
 */
export function transformToOpenIssueAgeData(stats: SecurityIssueStats | undefined): OpenIssueAgeData {
  const defaultAgeDistribution = [
    { label: "0-7 days", count: 0, percentage: 0, color: "bg-green-500" },
    { label: "8-30 days", count: 0, percentage: 0, color: "bg-yellow-500" },
    { label: "30+ days", count: 0, percentage: 0, color: "bg-red-500" },
  ];

  if (!stats) {
    return {
      averageAge: 0,
      ageDistribution: defaultAgeDistribution,
    };
  }
  return {
    averageAge: stats.open_issue_age?.average_days ?? 0,
    ageDistribution: stats.open_issue_age?.distribution ?? defaultAgeDistribution,
  };
}

/**
 * Transform API stats to status trend chart data format
 */
export function transformToStatusTrendData(stats: SecurityIssueStats | undefined): StatusTrendData[] {
  if (!stats) {
    return [];
  }
  return (stats.status_trend ?? []).map((item: StatusTrendItem, index: number) => {
    let formattedDate = `Day ${index + 1}`;
    const day = index + 1;

    try {
      if (item.date) {
        const parsedDate = parseISO(item.date);
        if (!isNaN(parsedDate.getTime())) {
          formattedDate = format(parsedDate, "MMM dd");
        }
      }
    } catch (err) {
      console.warn(`Failed to parse date: ${item.date}`, err);
    }

    return {
      day,
      date: formattedDate,
      opened: item.opened ?? 0,
      resolved: item.resolved ?? 0,
      inReview: item.in_review ?? 0,
    };
  });
}

export function getSeverityColor(severity: string): string {
  switch (severity.toUpperCase()) {
    case "CRITICAL":
      return "bg-red-100 text-red-800 hover:bg-red-100";
    case "HIGH":
      return "bg-orange-100 text-orange-800 hover:bg-orange-100";
    case "MEDIUM":
      return "bg-yellow-100 text-yellow-800 hover:bg-yellow-100";
    case "LOW":
      return "bg-blue-100 text-blue-800 hover:bg-blue-100";
    default:
      return "bg-gray-100 text-gray-800 hover:bg-gray-100";
  }
}

export function getStatusColor(status: string): string {
  switch (status.toLowerCase()) {
    case "success":
    case "protected":
      return "text-green-600 bg-green-50";
    case "blocked":
    case "warning":
    case "vulnerable":
      return "text-orange-600 bg-orange-50";
    default:
      return "text-gray-600 bg-gray-50";
  }
}

export function getIssueStatusColor(status: string): string {
  const colors: Record<string, string> = {
    OPEN: "bg-red-100 text-red-800 border-red-300",
    IN_REVIEW: "bg-yellow-100 text-yellow-800 border-yellow-300",
    RESOLVED: "bg-green-100 text-green-800 border-green-300",
    FALSE_POSITIVE: "bg-gray-100 text-gray-800 border-gray-300",
    SUPPRESSED: "bg-purple-100 text-purple-800 border-purple-300",
  };
  return colors[status] || "bg-gray-100 text-gray-800 border-gray-300";
}

export function getStatusBorderColor(status: string): string {
  switch (status.toLowerCase()) {
    case "protected":
      return "border-green-500 bg-green-50 text-green-900";
    case "vulnerable":
      return "border-red-500 bg-red-50 text-red-900";
    case "warning":
      return "border-orange-500 bg-orange-50 text-orange-900";
    default:
      return "border-gray-500 bg-gray-50 text-gray-900";
  }
}

export function getRiskColor(risk: string): string {
  switch (risk.toLowerCase()) {
    case "critical":
      return "bg-red-100 text-red-800";
    case "high":
      return "bg-orange-100 text-orange-800";
    case "medium":
      return "bg-yellow-100 text-yellow-800";
    case "low":
      return "bg-blue-100 text-blue-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}

export function getConnectorColor(risk: string): string {
  switch (risk.toLowerCase()) {
    case "critical":
      return "border-red-500";
    case "high":
      return "border-orange-500";
    case "medium":
      return "border-yellow-500";
    case "low":
      return "border-blue-500";
    default:
      return "border-gray-300";
  }
}

export function getRiskScoreColor(score: number): string {
  if (score >= 0.9) return "text-red-600";
  if (score >= 0.7) return "text-orange-600";
  if (score >= 0.5) return "text-yellow-600";
  return "text-blue-600";
}

export function formatRiskScore(score: number): string {
  return `${Math.round(score * 100)}%`;
}

export function getRiskBadgeHexColor(risk: string): string {
  switch (risk.toLowerCase()) {
    case "critical":
      return "#dc2626";
    case "high":
      return "#ea580c";
    case "medium":
      return "#ca8a04";
    case "low":
      return "#2563eb";
    default:
      return "#6b7280";
  }
}

/**
 * Format a duration in milliseconds to a human-readable compact string.
 * Examples: "just now", "47m", "3h 12m", "14d 3h", "2y 14d"
 */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms)) return "\u2014";
  if (ms <= 0) return "just now";

  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return "just now";

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours < 24) {
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  if (days < 365) {
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  }

  const years = Math.floor(days / 365);
  const remainingDays = days % 365;
  return remainingDays > 0 ? `${years}y ${remainingDays}d` : `${years}y`;
}

export function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "unknown";
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);

  if (diffSeconds < 60) return "just now";
  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}
